import { useCallback, useEffect, useMemo, useState } from 'react';

import { CircleMinus, CirclePlus, History, SlidersVertical } from 'lucide-react';
import moment from 'moment';
import { Badge, Button, DefaultPopover, DefaultSheet, DefaultTable, Separator, SheetBody, Row, Col, Label, Select, FormGroup, DatePickerPreset, SheetFooter, List, ListItem, ButtonIcon } from 'twyne-new.ui';

import InfiniteScroll from '@Components/Grid/components/Table/InfiniteScroll';
import TextTruncated from '@Components/TextTruncated';
import { isDayBlocked } from '@Components/hierarchical-report/utils';
import { usePathStore } from '@Modules/path/views/paths/store';
import { formatDateTime } from '@Utils/index';

import maskDefinition from './maskDefinition';

const ACTIONS_OPTIONS = [
  { value: 'create-slot', label: 'Create Slot' },
  { value: 'update-slot', label: 'Update Slot' },
  { value: 'add-page', label: 'Add Page' },
  { value: 'remove-page', label: 'Remove Page' },
  { value: 'update-page', label: 'Update Page' },
  { value: 'start-experiment', label: 'Start Experiment' },
  { value: 'end-experiment', label: 'End Experiment' },
  { value: 'reset-experiment', label: 'Reset Experiment' },
]

export function VersionRow({ row, isExpanded }) {
  const { action } = row

  const masks = {
    parent: maskDefinition[action],
    children: row.subEvents?.map(subEvent => maskDefinition[subEvent.action]),
  }

  return <>
    {!isExpanded ? <>
      <TextTruncated maxLength={120}>{masks.parent({ ...row.changedValues })}</TextTruncated>
    </> : <>
      <List className='p-0'>
        <ListItem>
          <TextTruncated maxLength={120}>{masks.parent({ ...row.changedValues })}</TextTruncated>
        </ListItem>
        {masks.children.map((child, index) => <ListItem key={index}>
          {child({ ...row.subEvents[index].changedValues })}
        </ListItem>)}
      </List>
    </>}
  </>
}

function Filter({ users, actions, dates, setUsers, setActions, onChangeDates, onApply }) {
  const { lists: { users: { items: usersData, useLoad: useLoadUsers } } } = usePathStore()
  useLoadUsers()
  const [open, setOpen] = useState(false)
  const usersOptions = useMemo(() => usersData?.map(user => ({
    value: user.id,
    label: `${user.first_name} ${user.last_name}`,
  })), [usersData])

  return <>
    <DefaultPopover
      trigger={<Button size='sm' variant='ghost' color='accent'>
        <SlidersVertical size={16} />
      </Button>}
      contentProps={{ side: 'right' }}
      wrapperProps={{ open: open, onOpenChange: setOpen }}
    >
      Filter
      <Separator className='m-0 my-2' />
      <Row>
        <Col size="12">
          <FormGroup>
            <Label className="mb-1">
              Date
            </Label>
            <DatePickerPreset
              showHoursSelector={false}
              minimumNights={0}
              mask="MM/DD/YYYY"
              isOutsideRange={isDayBlocked}
              value={{
                $gte: dates.startDate,
                $lte: dates.endDate,
              }}
              onChange={onChangeDates}
            />
          </FormGroup>
        </Col>
        <Col size="12">
          <FormGroup>
            <Label className="mb-1">Action</Label>
            <Select options={ACTIONS_OPTIONS} onChange={options => setActions(options.map(option => option.value))} value={actions} clearable multi />
          </FormGroup>
        </Col>
        <Col size="12">
          <FormGroup>
            <Label className="mb-1">User</Label>
            <Select options={usersOptions} onChange={options => setUsers(options.map(option => option.value))} value={users} clearable multi />
          </FormGroup>
        </Col>
      </Row>
      <div className='flex gap-2'>
        <Button color="accent" onClick={() => onApply({ users, actions, dates })}>Apply</Button>
        <Button variant='ghost' onClick={() => setOpen(false)}>Close</Button>
      </div>
    </DefaultPopover>
  </>
}

export default function Versions({ slotId }) {
  const { getSlotVersions } = usePathStore()

  const [isOpen, setIsOpen] = useState(false)
  const [data, setData] = useState([])
  const [rowsExpanded, setRowsExpanded] = useState([])
  const [users, setUsers] = useState([])
  const [actions, setActions] = useState([])
  const [total, setTotal] = useState(0)
  const [dates, setDates] = useState({
    startDate: moment().subtract(1, 'week'),
    endDate: moment(),
  })
  const [pagination, setPagination] = useState({
    page: 1,
    sizePerPage: 15,
  })

  const handleNextPage = () => {
    setPagination(pagination => ({
      ...pagination,
      sizePerPage: pagination.sizePerPage + pagination.sizePerPage,
    }))
  }
  const handleChangeDates = useCallback(searchDates => {
    setDates({
      startDate: searchDates.$gte,
      endDate: searchDates.$lte,
    })
  }, [])

  const fetchData = useCallback(async data => {
    const parmas = {}

    if (data.dates?.startDate && data.dates?.endDate) {
      const { startDate, endDate } = data.dates
      parmas.startDate = moment(startDate).utc().format('YYYY-MM-DD')
      parmas.endDate = moment(endDate).utc().format('YYYY-MM-DD')
    } else {
      parmas.startDate = moment().subtract(1, 'week').utc().format('YYYY-MM-DD')
      parmas.endDate = moment().utc().format('YYYY-MM-DD')
    }

    if (data.users?.length) {
      parmas.users = data.users
    }

    if (data.actions?.length) {
      parmas.actions = data.actions
    }

    parmas.options = pagination

    const { rows, total } = await getSlotVersions({ slotId, data: parmas })
    setData(rows)
    setTotal(total)
  }, [getSlotVersions, slotId, pagination])

  useEffect(() => {
    fetchData({})
  }, [fetchData])

  const toggleRow = id => {
    setRowsExpanded(prev => ({
      ...prev,
      [id]: !prev[id],
    }));
  }

  return <>
    <DefaultSheet
      trigger={<ButtonIcon content='Slot versions'>
        <History size={16} />
      </ButtonIcon>}
      title='Slot Versions'
      contentProps={{ size: 'xl' }}
      wrapperProps={{ open: isOpen, onOpenChange: setIsOpen }}
    >
      <SheetBody>
        <VersionsTable
          data={data}
          rowsExpanded={rowsExpanded}
          toggleRow={toggleRow}
          users={users}
          actions={actions}
          dates={dates}
          setUsers={setUsers}
          setActions={setActions}
          handleChangeDates={handleChangeDates}
          fetchData={fetchData}
          total={total}
          handleNextPage={handleNextPage}
        />
      </SheetBody>
      <SheetFooter>
        <Button variant='ghost' onClick={() => setIsOpen(false)}>
          Close
        </Button>
      </SheetFooter>
    </DefaultSheet>
  </>
}

const VersionsTable = ({ data, rowsExpanded, toggleRow, users, actions, dates, setUsers, setActions, handleChangeDates, fetchData, total, handleNextPage }) => {
  const columns = [
    {
      dataField: '',
      text: <Filter users={users} actions={actions} dates={dates} setUsers={setUsers} setActions={setActions} onChangeDates={handleChangeDates} onApply={fetchData} />,
      formatter: (_, row) => <Button size='sm' variant='ghost' color='accent' onClick={() => toggleRow(row.id)}>
        {rowsExpanded[row.id] ? <CircleMinus size={16} /> : <CirclePlus size={16} />}
      </Button>,
    }, {
      dataField: 'user',
      text: "User",
      formatter: user => `${user && `${user.first_name} ${user.last_name}` || 'N/A'}`,
    }, {
      dataField: 'createdAt',
      text: "Date",
      formatter: createdAt => formatDateTime(createdAt),
    }, {
      dataField: 'action',
      text: "Description",
      formatter: action => <Badge size='sm' variant='outline'>{action}</Badge>,
    }, {
      dataField: '',
      text: "Changes",
      formatter: (_, row) => <VersionRow row={row} isExpanded={rowsExpanded[row.id]} />,
    },
  ]

  return <DefaultTable
    keyField="id"
    data={data || []}
    columns={columns}
    noDataIndication={() => 'No versions yet.'}
    footer={<InfiniteScroll onVisit={handleNextPage} hasMore={total > data.length} />}
  />
}
